// Admissions domain types — kept aligned with backend models in
// backend/src/models/admissions/*. See docs/admission-module-redesign.md §3
// for the canonical schema definitions.

import type { Pagination } from './index';

// ─── Shared primitive refs ──────────────────────────────────────────────────

export interface IdNamePair { _id: string; name: string }
export type { Pagination };

// ─── Application form ──────────────────────────────────────────────────────

export type AdmissionFieldType =
  | 'text' | 'textarea' | 'number' | 'email' | 'phone'
  | 'date' | 'dropdown' | 'multiselect' | 'boolean'
  // The built-in applicant photo only (key `photo`) — saved on the person.
  | 'photo';
// No 'file' type: documents are requested only through the cycle's
// document checklist (one way to ask for and collect them).

export interface AdmissionFieldOption { value: string; label: string }

export interface AdmissionFieldValidation {
  min?: number; max?: number;
  minLength?: number; maxLength?: number;
  pattern?: string;
}

export interface AdmissionField {
  key: string;
  label: string;
  type: AdmissionFieldType;
  required: boolean;
  enabled: boolean;
  isBuiltIn: boolean;
  isSystem: boolean;
  /** Tagged fields are stripped from responses for callers without the
   *  `admissions_read_sensitive` scope. */
  isSensitive?: boolean;
  order: number;
  placeholder?: string;
  helpText?: string;
  validation?: AdmissionFieldValidation;
  options?: AdmissionFieldOption[];
  defaultValue?: any;
}

export interface AdmissionFormSection {
  key: string;
  title: string;
  order: number;
  fields: AdmissionField[];
}

/** A built-in field and the built-in section it belongs in (GET /admission-cycles/built-in-fields). */
export interface BuiltInFormField {
  section: { key: string; title: string };
  field: AdmissionField;
}

// ─── Scoring ───────────────────────────────────────────────────────────────

export interface CustomCriterion {
  key: string;
  label: string;
  maxMarks: number;
  enabled: boolean;
  order: number;
  description?: string;
}

export type MeritComponentSource = 'test' | 'interview' | 'custom' | 'previousPercentage';

export interface MeritComponent {
  source: MeritComponentSource;
  key?: string;
  weight: number;
  normalize: boolean;
}

export type MeritTieBreaker =
  | 'test_score' | 'interview_score' | 'previous_percentage'
  | 'dob_older' | 'dob_younger' | 'application_date';

export interface MeritFormula {
  components: MeritComponent[];
  mode: 'weighted-sum' | 'weighted-average';
  rounding: number;
  scaleTo100: boolean;
  tieBreakers?: MeritTieBreaker[];
}

// ─── AdmissionCycle (reshaped) ─────────────────────────────────────────────

export type AdmissionCycleStatus = 'Draft' | 'Open' | 'Closed' | 'Archived';

export interface AdmissionIntake {
  /** The public online form. */
  online: boolean;
  /** Staff entries: walk-ins and bulk upload. */
  walkIn: boolean;
  /** Classes not taking applications right now (e.g. full). */
  closedClassIds: string[];
}

/** The application window on the school's calendar. */
export interface ApplicationWindow {
  state: 'not_open' | 'open' | 'closed';
  today: string;
  /** Days until the close date; null with no close date (rolling). */
  daysLeft: number | null;
}

/** Where an intake channel stands today. */
export type IntakeChannelState = 'open' | 'not_started' | 'ended' | 'paused' | 'not_yet' | 'past_deadline';

export interface IntakeChannelNow {
  open: boolean;
  state: IntakeChannelState;
  /** Why it isn't taking applications, or null. */
  reason: string | null;
}

export interface AdmissionIntakeNow {
  online: IntakeChannelNow;
  walkIn: IntakeChannelNow;
  window: ApplicationWindow;
}

export interface AdmissionCycleSchedule {
  applicationOpen: string;
  /** Null for rolling admissions: the online form stays open until paused. */
  applicationClose: string | null;
  documentReviewBy?: string | null;
  testWindow?: { from: string; to: string } | null;
  interviewWindow?: { from: string; to: string } | null;
  meritPublishDates: string[];
  admissionFeeDeadline: string;
  classesBeginOn: string;
}

/** One class's test sitting. `date` is a calendar date, `reportingTime` "HH:mm". */
export interface TestSitting {
  classId: string;
  date?: string | null;
  reportingTime?: string;
  venue?: string;
}

export interface SeatMatrixEntry {
  classId: string;
  totalSeats: number;
  reservedQuotaSeats?: number;
  waitlistSize: number;
  overbookPercent?: number;
}

export interface EligibilityRule {
  classId: string;
  /** Age limits in completed months on `date` (2 years 6 months = 30). */
  ageOn?: { date: string; minMonths?: number; maxMonths?: number } | null;
  minPreviousPercentage?: number | null;
  requiredSubjects?: string[];
  gender?: 'male' | 'female' | 'any';
}

export interface AdmissionFeeHead {
  classId?: string | null;
  label: string;
  amount: number;
  refundable: boolean;
  optional: boolean;
}

export interface RefundSlab { beforeDays: number; percentage: number }

export interface RefundPolicy {
  slabs: RefundSlab[];
  securityDepositAlwaysRefundable: boolean;
}

export interface TestPaperConfig {
  key: string;
  subject: string;
  maxMarks: number;
}

/** `marks` questions are scored and add up to the round's total; `choice`
 *  (one of `options`) and `note` (free text) are recorded, never scored. */
export type RubricItemKind = 'marks' | 'choice' | 'note';

export interface InterviewRubricItem {
  criterion: string;
  kind?: RubricItemKind;
  maxMarks: number;
  options?: string[];
  /** Choice / note questions that must be answered. */
  required?: boolean;
}

export interface InterviewRoundWithRubric {
  key: string;
  name: string;
  /** Only the wording differs: an observation reads "Observation". */
  kind?: 'interview' | 'observation';
  maxMarks: number;
  /** 1 = whole marks, 0.5 = half marks. */
  markStep?: number;
  rubric: InterviewRubricItem[];
}

export interface OfferTerm {
  key: string;
  label: string;
  content: string;      // markdown
  required: boolean;
}

export interface AdmissionsStageToggles {
  admitCardEnabled: boolean;
  testEnabled: boolean;
  interviewEnabled: boolean;
  customMarksEnabled: boolean;
}

export interface DocumentChecklistItem {
  key: string;
  label: string;
  required: boolean;
  fileTypes: string[];
}

export interface AdmitCardNumberFormat {
  prefix: string;
  includeYear: boolean;
  includeClassCode: boolean;
  padding: number;
  perCycleCounter: boolean;
}

/** What a group of classes goes through after applying. */
export interface AssessmentPathway {
  key: string;
  name: string;
  /** Ignored on the first (default) pathway. */
  classIds: string[];
  stages: AdmissionsStageToggles;
  testConfig: { maxMarks: number; passingMarks: number; papers: TestPaperConfig[] };
  interviewConfig: { rounds: InterviewRoundWithRubric[] };
  documentChecklist: DocumentChecklistItem[];
  scoringConfig: { customCriteria: CustomCriterion[]; meritFormula: MeritFormula };
}

export interface AdmissionCycle {
  _id: string;
  schoolId: string;
  sessionId: string | IdNamePair;

  name: string;
  description?: string;

  status: AdmissionCycleStatus;
  schedule: AdmissionCycleSchedule;

  allowedClassIds: Array<string | IdNamePair>;

  seatMatrix: SeatMatrixEntry[];
  eligibility: EligibilityRule[];

  applicationFeeAmount: number;
  admissionFeeHeads: AdmissionFeeHead[];
  refundPolicy: RefundPolicy;

  /** The application form — starts as EduBook's built-in form. */
  formTemplate: { sections: AdmissionFormSection[] };
  /** How applicants are assessed, one pathway per group of classes. The
   *  first is the default and takes every class the others don't. */
  pathways: AssessmentPathway[];

  admitCardNumberFormat: AdmitCardNumberFormat;
  admitCardInstructions: string[];
  /** When and where each class sits the test; admit cards read it live. */
  testSchedule?: TestSitting[];

  offerTerms: OfferTerm[];
  /** Days a family has to accept an offer before it lapses on its own. */
  offerValidityDays?: number;
  prospectusFilename?: string | null;

  /** Whether new applications come in — by channel and by class — without ending the cycle. */
  intake: AdmissionIntake;
  /** Today's state of each channel, as the server judges it (status, switches, dates). */
  intakeNow: AdmissionIntakeNow;

  publicToken?: string | null;
  publicTokenRotatedAt?: string | null;

  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

/** What can't change on an open cycle and why — mirror of the backend's
 *  OpenCycleLocks (services/admissions/openCycleEdits.ts). Null when the
 *  cycle isn't open. */
export interface CycleEditLocks {
  admitCardNumbering: string | null;
  pathways: Record<string, { test: string | null; interview: string | null; custom: string | null; merit: string | null }>;
  /** Classes whose admission fees are fixed — families hold offers at them. */
  feeLockedClassIds: string[];
  /** Form fields applicants have answered. */
  answeredFieldKeys: string[];
}

/** Cycle stats endpoint response — status → count map. */
export interface AdmissionCycleStats {
  byStatus: Partial<Record<ApplicationStatus, number>>;
  total: number;
}

/** Seat-matrix snapshot — per-class LIVE vs hard cap. */
export interface SeatSnapshot {
  classId: string;
  totalSeats: number;
  overbookPercent: number;
  hardCap: number;
  live: number;
  remaining: number;
}

// ─── AdmissionApplication (reshaped) ───────────────────────────────────────

export type ApplicationStatus =
  | 'Draft' | 'Submitted' | 'AppFeePending' | 'UnderReview' | 'DocsRequired'
  | 'Ineligible' | 'AssessmentScheduled' | 'Absent' | 'Assessed'
  | 'Offered' | 'OfferAccepted' | 'OfferDeclined' | 'OfferLapsed'
  | 'Waitlisted' | 'NotAdmitted' | 'Rejected'
  | 'ConfirmedConditional' | 'AdmFeePending' | 'Confirmed' | 'Enrolled' | 'Withdrawn';

export const APPLICATION_TERMINAL_STATUSES: ApplicationStatus[] = [
  'Ineligible', 'OfferDeclined', 'OfferLapsed', 'NotAdmitted', 'Rejected', 'Enrolled', 'Withdrawn',
];

/** An offered seat (or a waitlist place) is counted per class, so from here
 *  on the class applied for can't be corrected. Mirrors the server. */
export const CLASS_LOCKED_STATUSES: ApplicationStatus[] = [
  'Offered', 'OfferAccepted', 'ConfirmedConditional', 'AdmFeePending', 'Confirmed', 'Enrolled', 'Waitlisted',
];

export type ApplicationSource =
  | 'public_link' | 'walk_in' | 'referral' | 'web_form' | 'imported' | 'form_link';

export type GuardianRelation = 'father' | 'mother' | 'guardian';

export interface PendingGuardianCapture {
  relation: GuardianRelation;
  name: string;
  cnic: string;
  phone: string;
  email?: string;
  occupation?: string;
}

export type DocumentStatus = 'pending' | 'uploaded' | 'verified' | 'rejected';

export interface ApplicationDocument {
  _id?: string;
  key: string;
  fileName: string;
  filename: string;
  mimeType: string;
  size: number;
  uploadedAt: string;
  status: DocumentStatus;
  verifiedBy?: string | IdNamePair | null;
  verifiedAt?: string | null;
  rejectedReason?: string;
  physicallyVerifiedAt?: string | null;
  physicallyVerifiedBy?: string | IdNamePair | null;
}

export interface AdmitCardSnapshot {
  rollNumber: string;
  issuedAt: string;
  testDate?: string | null;
  testTime?: string;
  testVenue?: string;
  testCenterRoom?: string;
  seatNumber?: string;
}

export interface TestScore {
  paperKey: string;
  subject: string;
  maxMarks: number;
  obtained: number;
  absent: boolean;
  enteredBy?: string | IdNamePair | null;
  enteredAt?: string | null;
  moderatedBy?: string | IdNamePair | null;
  moderatedAt?: string | null;
}

export interface RubricScore {
  criterion: string;
  kind?: RubricItemKind;
  maxMarks: number;
  obtained?: number;
  answer?: string;
  note?: string;
}

export type InterviewRecommendation =
  | 'strongly_recommend' | 'recommend' | 'hold' | 'do_not_recommend';

export interface InterviewRoundScore {
  roundKey: string;
  totalMarks: number;
  rubricScores: RubricScore[];
  recommendation?: InterviewRecommendation;
  comments?: string;
  interviewer?: string;
  enteredBy?: string | IdNamePair | null;
  enteredAt?: string | null;
}

export type EligibilitySeverity = 'pass' | 'warn' | 'fail';

/** An exception to the eligibility rules, granted by the school. */
export interface EligibilityWaiver {
  by?: string | IdNamePair | null;
  at: string;
  reason: string;
}

export interface EligibilityFlag {
  rule: 'age' | 'min_marks' | 'required_subjects' | 'gender';
  severity: EligibilitySeverity;
  message: string;
}

export type ConcessionRequestType =
  | 'sibling' | 'staff_child' | 'merit' | 'need_based' | 'hafiz'
  | 'sports' | 'orphan' | 'shuhada' | 'disability' | 'corporate'
  | 'early_bird' | 'other';

export type ConcessionRequestStatus = 'pending' | 'approved' | 'rejected';

export interface ConcessionRequest {
  type: ConcessionRequestType;
  mode: 'percent' | 'flat';
  value: number;
  reason?: string;
  evidenceDocuments?: string[];
  requestedBy?: string | IdNamePair | null;
  requestedAt: string;
  approvedBy?: string | IdNamePair | null;
  approvedAt?: string | null;
  status: ConcessionRequestStatus;
  carryToFees: boolean;
  mintedFeeConcessionId?: string | null;
  mintedFeeWaiverId?: string | null;
}

/** One concession request with the application it belongs to (Finance → Approvals). */
export interface ApplicationConcessionRequest {
  applicationId: string;
  /** Position in the application's concessionRequests — the decide endpoint's key. */
  index: number;
  name: string;
  fatherName?: string;
  request: ConcessionRequest;
  requestedByName: string | null;
  decidedByName: string | null;
}

export interface ConcessionRequestList {
  items: ApplicationConcessionRequest[];
  pagination: Pagination;
}

export interface TermAcceptance {
  termKey: string;
  acceptedAt: string;
  acceptedBy: string | IdNamePair;
  onBehalfOfGuardianId: string | IdNamePair;
  evidenceFilename?: string;
}

export interface OfferCondition {
  kind: 'pending_result' | 'pending_document' | 'pending_fee' | 'other';
  description: string;
  dueBy?: string | null;
  resolvedAt?: string | null;
  resolvedBy?: string | IdNamePair | null;
}

export interface OfferSnapshot {
  issuedAt: string;
  issuedBy: string | IdNamePair;
  expiresAt: string;
  respondedAt?: string | null;
  response?: 'accepted' | 'declined' | 'deferred';
  declineReason?: string;
  deferToCycleId?: string | null;
  termsAccepted: TermAcceptance[];
  conditions?: OfferCondition[];
}

export interface RejectionSnapshot {
  issuedAt: string;
  issuedBy: string | IdNamePair;
  reason?: string;
}

export interface ConfirmationSnapshot {
  issuedAt: string;
  issuedBy: string | IdNamePair;
}

export interface WaitlistEntry {
  /** Merit rank when waitlisted (null when the cycle doesn't rank). */
  rank: number | null;
  enteredAt: string;
  promotedAt?: string | null;
  promotedFromRank?: number | null;
}

export interface WithdrawalEntry {
  at: string;
  by: string | IdNamePair;
  reason: string;
  refundComputedAmount: number;
  refundIssuedAt?: string | null;
  refundIssuedBy?: string | IdNamePair | null;
  refundReference?: string;
}

export interface TimelineEntry {
  from?: ApplicationStatus | null;
  to?: ApplicationStatus;              // present on state-transition entries
  event?: string;                      // present on operational-event entries (e.g. 'admit_card_issued')
  label?: string;                      // human-readable summary for event entries
  at: string;
  by?: string | IdNamePair | null;
  reason?: string;
  meta?: Record<string, any>;
}

/** One corrected answer, as recorded on the 'details_corrected' timeline entry. */
export interface DetailChange { key: string; from: unknown; to: unknown }

export interface PendingDecisionOverride {
  proposedBy: string | IdNamePair;
  proposedAt: string;
  proposedState: ApplicationStatus;
  rationale: string;
  /** closed = the application moved on without needing the approval. */
  status: 'pending' | 'approved' | 'rejected' | 'closed';
  decidedBy?: string | IdNamePair | null;
  decidedAt?: string | null;
  decisionNote?: string;
}

export interface EnrollmentConfirmation {
  confirmedAt: string;
  confirmedBy: string | IdNamePair;
  changesApplied: Record<string, { from: any; to: any }>;
}

export interface AdmissionApplication {
  _id: string;
  schoolId: string;
  cycleId: string | IdNamePair;
  classId: string | IdNamePair;
  /** The applicant's reference (`APP-2026-00042`). The GR number comes at enrollment. */
  applicationNumber: string;

  /** `grNumber` is set once enrolled — or already, for a returning student. */
  personId: string | { _id: string; grNumber?: string; name?: string; kind?: string; photoFilename?: string };
  familyId: string | { _id: string; displayName?: string; status?: string };

  name: string;
  fatherName: string;
  fatherPhone: string;
  address: string;
  candidateContact?: string;
  email?: string;
  dob?: string | null;
  gender?: 'male' | 'female' | 'other';
  cnic: string;

  pendingGuardians: PendingGuardianCapture[];
  familyResolvedAt?: string | null;
  familyResolvedBy?: string | IdNamePair | null;

  source: ApplicationSource;

  previousSchool?: string;
  previousPercentage?: number;

  formData: Record<string, any>;
  documents: ApplicationDocument[];
  /** When staff last emailed the family to resend rejected documents. */
  resubmissionRequest?: { at: string; by: string; message: string } | null;

  admitCard?: AdmitCardSnapshot | null;

  testScores: TestScore[];
  interviewRounds: InterviewRoundScore[];

  customScores: Record<string, number>;
  totalCustomMarks: number;

  computedMeritScore?: number | null;
  meritRank?: number | null;
  /** Why the applicant is unranked ("Panel interview", …); empty when ranked. */
  meritGaps?: string[];
  /** Entrance-test result against the pass mark; null until every paper is marked. */
  testPassed?: boolean | null;
  isSpecialCase: boolean;

  eligibilityFlags: EligibilityFlag[];
  /** Set when the school lets the applicant continue despite failing the rules. */
  eligibilityWaiver?: EligibilityWaiver | null;

  feeInvoiceIds: string[];
  concessionRequests: ConcessionRequest[];

  offer?: OfferSnapshot | null;
  rejection?: RejectionSnapshot | null;
  confirmation?: ConfirmationSnapshot | null;

  waitlist?: WaitlistEntry | null;
  withdrawal?: WithdrawalEntry | null;

  status: ApplicationStatus;
  timeline: TimelineEntry[];

  studentId?: string | null;
  enrollmentId?: string | null;
  enrollmentConfirmation?: EnrollmentConfirmation | null;

  statusToken: string;
  statusTokenRotatedAt?: string | null;

  pendingDecisionOverrides: PendingDecisionOverride[];

  /** Guardians of a linked family — only on `include=export` listings. */
  guardians?: PendingGuardianCapture[];

  createdBy?: string | IdNamePair | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── AdmissionEnquiry ───────────────────────────────────────────────────────

/** A family interested in a future admission, before they apply. */
export type AdmissionEnquiryStatus = 'new' | 'called' | 'applied' | 'not_interested';

export interface AdmissionEnquiry {
  _id: string;
  childName: string;
  dob?: string | null;
  /** Academic year by its start year: 2027 = 2027–28. */
  forYear: number;
  classId: IdNamePair;
  parentName: string;
  parentPhone: string;
  note?: string;
  status: AdmissionEnquiryStatus;
  calledAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What staff send to add or edit an enquiry. */
export interface AdmissionEnquiryInput {
  childName: string;
  dob: string | null;
  forYear: number;
  classId: string;
  parentName: string;
  parentPhone: string;
  note: string;
}

// ─── Paginated list envelopes ──────────────────────────────────────────────

export interface AdmissionCyclesListResponse { items: AdmissionCycle[]; pagination: Pagination }
export interface AdmissionApplicationsListResponse {
  items: AdmissionApplication[];
  pagination: Pagination;
  /** Staff id → name, only on `include=export` listings. */
  users?: Record<string, string>;
}
export interface AdmissionEnquiriesListResponse {
  items: AdmissionEnquiry[];
  pagination: Pagination;
  /** How many in each status under the same filters (status aside). */
  counts: Record<AdmissionEnquiryStatus, number>;
}

// ─── Status-link + Student 360 shapes ──────────────────────────────────────

export interface StatusLinkInvoice {
  _id: string;
  invoiceNumber: string;
  kind: 'tuition' | 'application_fee' | 'admission_fee' | 'other';
  dueDate: string;
  status: string;
  total: number;
  outstanding: number;
}

/** The parts of an application an admit card prints. */
export type AdmitCardApplicant = Pick<AdmissionApplication, 'name' | 'fatherName' | 'gender' | 'classId' | 'applicationNumber' | 'personId' | 'admitCard'>;

/** The parts of a cycle an admit card prints. */
export interface AdmitCardCycle {
  name: string;
  /** The card shows the papers of the applicant's pathway. */
  pathways: Array<{
    key: string;
    classIds: string[];
    stages?: { testEnabled?: boolean };
    testConfig?: { maxMarks?: number; papers?: TestPaperConfig[] };
    /** Round kinds only — the card names the day ("Observation date"). */
    interviewConfig?: { rounds?: Array<Pick<InterviewRoundWithRubric, 'kind'>> };
  }>;
  admitCardInstructions?: string[];
  testSchedule?: TestSitting[];
}

export interface StatusLinkResponse {
  application: {
    _id: string;
    status: ApplicationStatus;
    name: string;
    cycle: { _id?: string; name?: string; schedule?: AdmissionCycleSchedule };
    class: IdNamePair | string;
    applicationNumber: string;
    /** Set once enrolled (or for a returning student). */
    grNumber?: string | null;
    photoFilename?: string | null;
  };
  nextStep: string;
  /** What the family's browser needs to draw the admit card — the same card staff print. */
  admitCard: null | { applicant: AdmitCardApplicant; cycle: AdmitCardCycle };
  school?: { name: string; logo?: string | null };
  invoices: StatusLinkInvoice[];
  timeline: Array<{ at: string; to: ApplicationStatus }>;
  /** Present while a seat is offered — the last day to accept. */
  offer?: { expiresAt: string } | null;
  /** The documents the school asked for and where each stands. */
  documents?: DocumentStanding[];
  canUploadDocuments?: boolean;
  /** Whether the form asks for the applicant's photo, and must have one. */
  photoField?: { enabled: boolean; required: boolean };
  eligibilityIssues?: string[];
}

export interface DocumentStanding {
  key: string;
  label: string;
  required: boolean;
  fileTypes: string[];
  status: DocumentStatus | 'missing';
  fileName: string | null;
  rejectedReason: string | null;
}
